import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { Mic, MicOff, Volume2, VolumeX, Play, Pause, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VoiceControl = forwardRef(({ onTranscript, autoSend = false, disabled = false }, ref) => {
  const { t } = useTranslation();
  const [isListening, setIsListening] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [mode, setMode] = useState('conversation'); // 'conversation' or 'record'
  const [transcript, setTranscript] = useState('');
  const [recordingStartTime, setRecordingStartTime] = useState(null);
  
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isListeningRef = useRef(false);
  const modeRef = useRef('conversation');
  const transcriptRef = useRef('');

  useEffect(() => {
    isListeningRef.current = isListening;
  }, [isListening]);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);

  useEffect(() => {
    // Initialize Web Speech API only once
    if (!recognitionRef.current && ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'pt-BR';

      recognitionRef.current.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
            console.log('✅ Transcrição final:', transcript);
          } else {
            interimTranscript += transcript;
            console.log('⏳ Transcrição parcial:', transcript);
          }
        }

        // Only process FINAL transcripts to avoid duplicates
        if (finalTranscript) {
          console.log('📝 Transcrição completa detectada:', finalTranscript);
          const newTranscript = transcript + finalTranscript;
          setTranscript(newTranscript);
          transcriptRef.current = newTranscript; // Update ref immediately
          
          if (modeRef.current === 'conversation' && autoSend) {
            // In conversation mode, send immediately
            console.log('🚀 Modo conversa - enviando automaticamente');
            onTranscript(finalTranscript.trim(), true);
            setTranscript('');
            transcriptRef.current = '';
          }
          // In record mode, just accumulate (don't call onTranscript yet)
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.log('🔴 Speech recognition error:', event.error);
        
        // Ignore expected errors
        if (event.error === 'no-speech' || event.error === 'aborted') {
          console.log('ℹ️ Erro esperado, ignorando:', event.error);
          return;
        }
        
        // Only show error for unexpected issues
        console.error('❌ Erro inesperado no reconhecimento de voz:', event.error);
        toast.error(t('voice.recognitionError') + ': ' + event.error);
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        console.log('🏁 Recognition ended, isListening:', isListeningRef.current, 'mode:', modeRef.current);
        
        // In record mode, send the accumulated transcript
        if (modeRef.current === 'record' && !isListeningRef.current) {
          const currentTranscript = transcriptRef.current;
          console.log('📤 Ao terminar gravação, transcript:', currentTranscript);
          
          if (currentTranscript && currentTranscript.trim()) {
            console.log('✅ Enviando transcrição acumulada:', currentTranscript);
            onTranscript(currentTranscript.trim(), true);
            setTranscript('');
            transcriptRef.current = '';
            toast.success(t('voice.recordingSent'));
          } else {
            console.log('⚠️ Nenhuma transcrição ao terminar');
            toast.warning(t('voice.noSpeechDetected'));
          }
        }
        
        // In conversation mode, restart if still listening
        if (isListeningRef.current && modeRef.current === 'conversation') {
          // Restart in conversation mode
          console.log('🔄 Reiniciando em modo conversa...');
          try {
            recognitionRef.current.start();
          } catch (e) {
            console.error('Error restarting recognition:', e);
          }
        }
      };
    }

    // Cleanup only on unmount
    return () => {
      console.log('🧹 Cleanup - component unmounting');
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          console.error('Error stopping on cleanup:', e);
        }
      }
    };
  }, []); // Empty dependency array - only run once on mount

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    playAudio,
    stopAudio
  }));

  const toggleListening = async () => {
    if (!recognitionRef.current) {
      toast.error(t('voice.notSupported'));
      return;
    }

    if (isListening) {
      console.log('⏹️ Parando gravação...');
      
      // Check minimum recording time (1 second)
      const recordingDuration = Date.now() - recordingStartTime;
      if (recordingDuration < 1000) {
        toast.warning(t('voice.speakAtLeast1Second'));
        return;
      }
      
      if (mode === 'record') {
        // In record mode, wait for final processing
        toast.info(t('voice.processingSpeech'));
        
        setTimeout(() => {
          recognitionRef.current.stop();
          setIsListening(false);
          setRecordingStartTime(null);
          
          // Use ref to get the latest transcript value
          const currentTranscript = transcriptRef.current;
          console.log('📤 Verificando transcrição após delay:', currentTranscript);
          
          if (currentTranscript && currentTranscript.trim()) {
            // Send accumulated transcript
            console.log('✅ Enviando texto acumulado:', currentTranscript);
            onTranscript(currentTranscript.trim(), true);
            setTranscript('');
            transcriptRef.current = '';
            toast.success(t('voice.recordingSent'));
          } else {
            console.log('⚠️ Nenhuma transcrição capturada após delay');
            toast.warning(t('voice.noSpeechDetected'));
          }
        }, 1000); // Wait 1 second for final processing
      } else {
        // Conversation mode - stop immediately
        recognitionRef.current.stop();
        setIsListening(false);
        setRecordingStartTime(null);
        toast.info(t('voice.listeningStopped'));
      }
    } else {
      console.log('▶️ Iniciando gravação/escuta...');
      
      // Check microphone permission first
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(track => track.stop()); // Stop the test stream
        console.log('✅ Permissão de microfone concedida');
      } catch (error) {
        console.error('❌ Erro de permissão de microfone:', error);
        toast.error(t('voice.microphonePermission'));
        return;
      }
      
      setTranscript('');
      
      try {
        recognitionRef.current.start();
        setIsListening(true);
        setRecordingStartTime(Date.now());
        
        if (mode === 'conversation') {
          toast.success(t('voice.conversationModeActivated'));
        } else {
          toast.success(t('voice.recordingModeActivated'));
        }
      } catch (error) {
        console.error('❌ Erro ao iniciar reconhecimento:', error);
        toast.error('Erro ao iniciar reconhecimento de voz');
      }
    }
  };

  const playAudio = async (text) => {
    try {
      setIsPlaying(true);
      
      // Use Web Speech API for TTS (free, browser-native)
      if ('speechSynthesis' in window) {
        // Cancel any ongoing speech
        window.speechSynthesis.cancel();
        
        // Wait for voices to load if needed
        let voices = window.speechSynthesis.getVoices();
        if (voices.length === 0) {
          await new Promise(resolve => {
            window.speechSynthesis.onvoiceschanged = () => {
              voices = window.speechSynthesis.getVoices();
              resolve();
            };
          });
        }
        
        console.log('🔊 Vozes disponíveis:', voices.map(v => `${v.name} (${v.lang})`));
        
        // Try to find the best Portuguese BR voice
        let selectedVoice = 
          voices.find(voice => voice.lang === 'pt-BR') ||
          voices.find(voice => voice.lang.startsWith('pt-BR')) ||
          voices.find(voice => voice.lang === 'pt-PT') ||
          voices.find(voice => voice.lang.startsWith('pt')) ||
          voices.find(voice => voice.name.toLowerCase().includes('portuguese')) ||
          voices.find(voice => voice.name.toLowerCase().includes('luciana')) || // Google pt-BR
          voices.find(voice => voice.name.toLowerCase().includes('joana')) ||   // Apple pt-BR
          voices.find(voice => voice.name.toLowerCase().includes('brasil'));
        
        if (selectedVoice) {
          console.log('✅ Voz selecionada:', selectedVoice.name, selectedVoice.lang);
        } else {
          console.log('⚠️ Nenhuma voz PT-BR encontrada, usando voz padrão');
          selectedVoice = voices[0];
        }
        
        // Create utterance
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'pt-BR';
        utterance.rate = playbackSpeed;
        utterance.pitch = 1.0;
        utterance.volume = 1.0;
        
        if (selectedVoice) {
          utterance.voice = selectedVoice;
        }
        
        utterance.onend = () => {
          console.log('✅ Áudio finalizado');
          setIsPlaying(false);
          setIsPaused(false);
          
          // Play next in queue if any
          if (audioQueueRef.current.length > 0) {
            const nextText = audioQueueRef.current.shift();
            playAudio(nextText);
          }
        };
        
        utterance.onerror = (error) => {
          console.error('❌ Erro ao reproduzir áudio:', error);
          toast.error('Erro ao reproduzir áudio');
          setIsPlaying(false);
        };
        
        window.speechSynthesis.speak(utterance);
        audioRef.current = utterance; // Store for pause/resume
        
        console.log('🔊 Reproduzindo com Web Speech API');
      } else {
        toast.error('Síntese de voz não suportada neste navegador');
        setIsPlaying(false);
      }
      
    } catch (error) {
      console.error('Error playing audio:', error);
      toast.error('Erro ao reproduzir áudio');
      setIsPlaying(false);
    }
  };

  const togglePlayPause = () => {
    if (!window.speechSynthesis) return;
    
    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
      setIsPaused(false);
      console.log('▶️ Áudio retomado');
    } else if (window.speechSynthesis.speaking) {
      window.speechSynthesis.pause();
      setIsPaused(true);
      console.log('⏸️ Áudio pausado');
    }
  };

  const stopAudio = () => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setIsPlaying(false);
    setIsPaused(false);
    audioQueueRef.current = [];
    console.log('🛑 Áudio parado');
  };

  const changeSpeed = () => {
    const speeds = [0.75, 1.0, 1.25, 1.5, 2.0];
    const currentIndex = speeds.indexOf(playbackSpeed);
    const nextSpeed = speeds[(currentIndex + 1) % speeds.length];
    
    setPlaybackSpeed(nextSpeed);
    
    // For Web Speech API, need to restart with new speed
    if (window.speechSynthesis && window.speechSynthesis.speaking) {
      toast.info(`Velocidade alterada para ${nextSpeed}x. A nova velocidade será aplicada na próxima mensagem.`);
    } else {
      toast.success(`Velocidade: ${nextSpeed}x`);
    }
  };

  const toggleMode = () => {
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
    
    const newMode = mode === 'conversation' ? 'record' : 'conversation';
    setMode(newMode);
    toast.success(newMode === 'conversation' ? t('voice.conversationMode') : t('voice.recordingMode'));
  };

  return (
    <div className="flex items-center gap-2">
      {/* Mode Toggle */}
      <Button
        variant="outline"
        size="sm"
        onClick={toggleMode}
        disabled={disabled || isListening}
        title={mode === 'conversation' ? 'Modo: Conversa contínua' : 'Modo: Gravar e enviar'}
        className={mode === 'conversation' ? 'bg-blue-50' : 'bg-orange-50'}
      >
        {mode === 'conversation' ? '💬' : '🎙️'}
      </Button>

      {/* Microphone Button with status */}
      <div className="relative">
        <Button
          variant={isListening ? 'default' : 'outline'}
          size="icon"
          onClick={toggleListening}
          disabled={disabled}
          className={isListening ? 'bg-red-500 hover:bg-red-600 animate-pulse' : ''}
          title={isListening ? 'Parar gravação/escuta' : 'Iniciar gravação/escuta'}
        >
          {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
        </Button>
        {isListening && (
          <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
            <span className="bg-red-500 text-white text-xs px-2 py-1 rounded animate-pulse">
              🎤 Ouvindo...
            </span>
          </div>
        )}
      </div>

      {/* Audio Controls */}
      {isPlaying && (
        <>
          <Button
            variant="outline"
            size="icon"
            onClick={togglePlayPause}
            title={isPaused ? 'Continuar' : 'Pausar'}
          >
            {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
          </Button>

          <Button
            variant="outline"
            size="icon"
            onClick={stopAudio}
            title="Parar áudio"
          >
            <VolumeX className="w-4 h-4" />
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={changeSpeed}
            title="Velocidade de reprodução"
          >
            {playbackSpeed}x
          </Button>
        </>
      )}

      {/* Transcript indicator */}
      {transcript && mode === 'record' && (
        <span className="text-xs text-gray-500 italic">
          {transcript.substring(0, 30)}...
        </span>
      )}
    </div>
  );
});

VoiceControl.displayName = 'VoiceControl';

export default VoiceControl;
